import { isString } from '@chakra-ui/utils';
import { format, parse, parseISO } from 'date-fns';
import { InventoryReportTypeEnum } from 'types/inventory';
import { Detail } from 'types/report';
import { SKU } from 'types/sku';
import { calculateLost } from 'utils/calculateLost';

export const toFormatDate = (date: any) => {
  let dateStringToObject;

  if (isString(date)) {
    dateStringToObject = parseISO(date.replace(/.$/, ''));
  } else {
    const dateTostring = date.toDateString();
    dateStringToObject = parse(dateTostring, 'EEE MMM dd yyyy', new Date());
  }
  const formattedDate = format(dateStringToObject as any, 'MM/dd/yyyy');

  return formattedDate;
};

export const returnRateEach = (item: any) => {
  const totalAccountFor = Number(item.ret + item.unu + item.end);
  const totalAvailable = Number(item.beg + item.del);
  if (totalAvailable === 0) {
    return 0;
  }
  const returnRateValue = ((totalAccountFor / totalAvailable) * 100).toFixed(2);
  return +returnRateValue > 0 ? Number(returnRateValue) : 0;
};

type Accumulator = {
  lost: number;
  used: number;
  ending: number;
  beginning: number;
  returnedDirty: number;
  totalReturned: number;
  totalDelivered: number;
  totalAvailable: number;
  returnedUnused: number;
  totalAccountFor: number;
};

export const calculateValues = (
  details: Detail<SKU>[] | undefined,
): Accumulator => {
  return (
    details?.reduce<Accumulator>(
      (acc, cur) => {
        const lost = calculateLost(cur);

        const used = Number(cur?.ret || '0') + lost;

        acc.lost += lost;
        acc.used += used;
        acc.ending += Number(cur.end || 0);
        acc.beginning += Number(cur.beg || 0);
        acc.totalDelivered += Number(cur.del || 0);

        acc.totalAvailable = acc.totalDelivered + acc.beginning;

        acc.returnedDirty += Number(cur.ret || 0);
        acc.returnedUnused += Number(cur.unu || 0);
        acc.totalReturned += Number(cur.ret || 0);
        acc.totalAccountFor = acc.totalReturned + acc.ending;

        return acc;
      },
      {
        lost: 0,
        used: 0,
        ending: 0,
        beginning: 0,
        returnedDirty: 0,
        totalReturned: 0,
        totalDelivered: 0,
        totalAvailable: 0,
        returnedUnused: 0,
        totalAccountFor: 0,
      },
    ) ?? {
      lost: 0,
      used: 0,
      ending: 0,
      beginning: 0,
      returnedDirty: 0,
      totalReturned: 0,
      totalDelivered: 0,
      totalAvailable: 0,
      returnedUnused: 0,
      totalAccountFor: 0,
    }
  );
};

export const returnRate = (values: any) => {
  const totalAccountFor = Number(
    values.returnedDirty + values.returnedUnused + values.ending,
  );
  const totalAvailable = Number(values.beginning + values.totalDelivered);
  if (totalAvailable === 0) {
    return 0;
  }
  const returnRateValue = ((totalAccountFor / totalAvailable) * 100).toFixed(2);
  return Number(returnRateValue) || 0;
};

type AccumulatorInvoicing = {
  lost: number;
  used: number;
  usedFees: number;
  replacementFees: number;
  totalLostFees: number;
  totalUsedFees: number;
  subtotal: number;
};

export const calculateInvoicingValues = (
  details: Detail<SKU>[] | undefined,
  site: any,
  reportType: any,
): AccumulatorInvoicing => {
  return (
    details?.reduce(
      (acc, cur) => {
        const lost = calculateLost(cur);

        const used = (Number(cur?.ret) || 0) + (lost || 0);

        //consider site's replacement and delivery price if found over item's
        const sitePricingDetails =
          site?.pricingDetails?.filter(
            (pd: any) => pd.itemSku === cur.sku._id,
          ) || [];
        const deliveryPrice =
          sitePricingDetails.length > 0
            ? sitePricingDetails[0].deliveryPrice
            : cur.sku.pricePerDelivery;

        const replacementPrice =
          sitePricingDetails.length > 0
            ? sitePricingDetails[0].replacementPrice
            : cur?.sku?.pricePerReplacement;

        acc.lost = lost + acc.lost;
        acc.replacementFees = (replacementPrice || 0) + acc.replacementFees;
        acc.totalLostFees = lost * (replacementPrice || 0) + acc.totalLostFees;

        acc.used = Number(used) + acc.used;
        acc.usedFees = (deliveryPrice || 0) + acc.usedFees;
        acc.totalUsedFees =
          Number(used) * (deliveryPrice || 0) + acc.totalUsedFees;
        acc.subtotal =
          reportType === InventoryReportTypeEnum.NO_INVENTORY
            ? acc.totalUsedFees + acc.totalLostFees
            : acc.totalLostFees;

        return acc;
      },
      {
        lost: 0,
        used: 0,
        usedFees: 0,
        replacementFees: 0,
        totalLostFees: 0,
        totalUsedFees: 0,
        subtotal: 0,
      },
    ) ?? {
      lost: 0,
      used: 0,
      usedFees: 0,
      replacementFees: 0,
      totalLostFees: 0,
      totalUsedFees: 0,
      subtotal: 0,
    }
  );
};
